import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Box, Typography, alpha, Alert } from '@mui/material';
import NetworkEditor from './NetworkEditor';
import { validateModel, getValidationMessage } from '../utils/modelValidation';

/**
 * AdvancedModelBuilder is the main component that integrates the neural network editor
 * into the existing application.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.layers - Array of layer configurations
 * @param {Function} props.setLayers - Function to update layers
 * @param {string} props.validationError - Error message to display if validation fails
 */
const AdvancedModelBuilder = forwardRef(({ layers, setLayers, validationError }, ref) => {
  const [modelValidation, setModelValidation] = useState({ isValid: true, issues: [] });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const networkEditorRef = useRef(null);
  
  // Debug: Log layers whenever they change
  useEffect(() => {
  }, [layers]);
  
  // Validate model whenever layers change or submission state changes
  useEffect(() => {
    const validation = validateModel(layers, isSubmitting);
    setModelValidation(validation);
  }, [layers, isSubmitting]);
  
  // Create a ref to hold our functions
  const functionRef = useRef({
    syncLayers: () => {
      // Force an immediate update of layers from the editor
      if (networkEditorRef.current && networkEditorRef.current.updateLayersFromNodes) {
        networkEditorRef.current.updateLayersFromNodes();
      }
    }
  });
  
  // Expose functions via ref
  useImperativeHandle(ref, () => ({
    generateModelBlueprintJSON: () => {
      if (networkEditorRef.current && networkEditorRef.current.generateModelBlueprintJSON) {
        return networkEditorRef.current.generateModelBlueprintJSON();
      }
      return { model_blueprint: { model_type: "sequential", layers: [] } };
    },
    validateModel: () => {
      // Sync layers first
      functionRef.current.syncLayers();
      
      // Set isSubmitting true to get stricter validation for submission
      setIsSubmitting(true);
      // Return the result of a full validation
      const strictValidation = validateModel(layers, true);
      setModelValidation(strictValidation);
      return strictValidation;
    },
    isValid: () => {
      // Sync layers first
      functionRef.current.syncLayers();
      
      // Perform strict validation for submission check
      const strictValidation = validateModel(layers, true);
      return strictValidation.isValid;
    },
    syncLayers: () => {
      functionRef.current.syncLayers();
    },
    fitView: () => {
      // Trigger fit view on the network editor
      if (networkEditorRef.current && networkEditorRef.current.fitView) {
        networkEditorRef.current.fitView();
      }
    }
  }));
  
  // Generate validation message if there are issues
  const validationMessage = getValidationMessage(modelValidation.issues);
  const hasValidationIssues = !modelValidation.isValid && modelValidation.issues.length > 0;
  
  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      minHeight: '600px',
      position: 'relative' // Add relative positioning for absolute positioning of error
    }}>
      {/* Network editor takes full height */}
      <Box sx={{ 
        flex: 1, 
        minHeight: 0,
        height: '100%'
      }}>
        <NetworkEditor 
          ref={networkEditorRef}
          layers={layers}
          setLayers={setLayers}
        />
      </Box>
      
      {/* Show validation errors if present - positioned at bottom left but avoiding overlap with zoom toolbar */}
      {(validationError || hasValidationIssues) && (
        <Box sx={{ 
          position: 'absolute',
          bottom: 20,
          left: 120, // Increased from 80 to 120 to avoid overlap with the newly styled controls
          maxWidth: '400px',
          zIndex: 1000,
          opacity: 0.9
        }}>
          {validationError && (
            <Box sx={{ 
              p: 1, 
              mb: 1,
              bgcolor: alpha('#f44336', 0.1), 
              color: '#f44336', 
              borderRadius: 1,
              fontSize: '0.875rem'
            }}>
              <Typography variant="body2">{validationError}</Typography>
            </Box>
          )}
          
          {hasValidationIssues && (
            <Alert 
              severity="warning" 
              sx={{ 
                mb: 1,
                '& .MuiAlert-message': {
                  whiteSpace: 'pre-line'
                }
              }}
            >
              <Typography variant="subtitle2" component="div" gutterBottom>
                ML Compatibility Check
              </Typography>
              <Typography variant="body2" component="div">
                {validationMessage}
              </Typography>
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
});

export default AdvancedModelBuilder; 